import React, { useState, useEffect, useRef } from 'react';
import { 
  Table, Button, Badge, Spinner, 
  Alert, Modal, Form, InputGroup, Card, Row, Col
} from 'react-bootstrap';
import { 
  FaEdit, FaTrash, FaPlus, FaSearch,
  FaMapMarkerAlt, FaMoneyBillWave, FaGlobeAmericas
} from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const OverchargeLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    location: '',
    additionalCharge: 10,
    centerLat: '',
    centerLon: '',
    radiusKm: 2
  });
  const [editMode, setEditMode] = useState(false);
  const autocompleteInput = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLocations();
    loadGoogleMapsScript();
  }, [searchTerm, currentPage]);

  const loadGoogleMapsScript = () => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  };

  const initAutocomplete = () => {
    if (window.google && autocompleteInput.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        autocompleteInput.current,
        { types: ['geocode'] }
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) return;

        setFormData(prev => ({
          ...prev,
          location: place.formatted_address,
          centerLat: place.geometry.location.lat(),
          centerLon: place.geometry.location.lng()
        }));
      });
    }
  };

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/overcharge-locations`, {
        headers: { 'x-auth-token': token },
        params: { 
          page: currentPage,
          search: searchTerm 
        }
      });
      
      setLocations(response.data.locations);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError(err.response?.data?.error || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      
      if (editMode) {
        await axios.put(`${process.env.REACT_APP_API_URL}/admin/overcharge-locations/${formData._id}`,
          formData,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Location updated successfully');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/admin/overcharge-locations`,
          formData,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Location added successfully');
      }
      
      setShowForm(false);
      fetchLocations();
    } catch (err) {
      console.error('Error saving location:', err);
      setError(err.response?.data?.error || 'Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/overcharge-locations/${locationToDelete._id}`,
        { headers: { 'x-auth-token': token } }
      );
      
      setShowDeleteModal(false);
      fetchLocations();
      toast.success('Location deleted successfully');
    } catch (err) {
      console.error('Error deleting location:', err);
      setError(err.response?.data?.error || 'Failed to delete location');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (location) => {
    setFormData(location);
    setEditMode(true);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      location: '',
      additionalCharge: 10,
      centerLat: '',
      centerLon: '',
      radiusKm: 2
    });
    setEditMode(false);
  };

  return (
    <div className="overcharge-locations-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Overcharge Locations</h2>
        <Button 
          variant="primary" 
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <FaPlus className="me-2" />
          Add New Location
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {showForm ? (
        <Card className="mb-4">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaMapMarkerAlt />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        placeholder="Search location"
                        required
                        ref={autocompleteInput}
                        onFocus={initAutocomplete}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Additional Charge ($)</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaMoneyBillWave />
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="additionalCharge"
                        value={formData.additionalCharge}
                        onChange={handleInputChange}
                        placeholder="Additional charge"
                        min="0"
                        step="0.01"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Latitude</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaGlobeAmericas />
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="centerLat"
                        value={formData.centerLat}
                        onChange={handleInputChange}
                        placeholder="Latitude"
                        step="any"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Longitude</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaGlobeAmericas />
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="centerLon"
                        value={formData.centerLon}
                        onChange={handleInputChange}
                        placeholder="Longitude"
                        step="any"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Radius (km)</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaMapMarkerAlt />
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="radiusKm"
                        value={formData.radiusKm}
                        onChange={handleInputChange}
                        placeholder="Radius in km"
                        min="0.1"
                        step="0.1"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2">
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : editMode ? (
                    'Update Location'
                  ) : (
                    'Add Location'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      ) : (
        <>
          <div className="mb-3">
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search locations"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Additional Charge</th>
                    <th>Coordinates</th>
                    <th>Radius</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.length > 0 ? (
                    locations.map((location) => (
                      <tr key={location._id}>
                        <td>{location.location}</td>
                        <td>${location.additionalCharge}</td>
                        <td>
                          {location.centerLat}, {location.centerLon}
                        </td>
                        <td>{location.radiusKm} km</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEdit(location)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                setLocationToDelete(location);
                                setShowDeleteModal(true);
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No overcharge locations found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </button>
                      </li>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <li 
                          key={page} 
                          className={`page-item ${currentPage === page ? 'active' : ''}`}
                        >
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        </li>
                      ))}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the location <strong>{locationToDelete?.location}</strong>?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OverchargeLocations;